import React, { useEffect, useState } from 'react'
import { numFormat } from '../../blockchainUtils/math'
import { formatDollarValue, formatTimestamp, formatTimestampDay } from '../charts/chartUtils'
import { TokenChartData } from '../charts/chartsApi'
import { Bar, BarChart, CartesianGrid, Cell, Line, LineChart, ReferenceArea, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

export type ChartTypes = 'Volume' | 'TVL' | 'Price' | 'Liquidity'
const chartHeight = 314

export function ChartsContainer(props: {
    options?: ChartTypes[]
    chartData: TokenChartData[]
    selectedChart: ChartTypes
    setSelectedChart: (type: ChartTypes) => void
    renderedChart?: JSX.Element | null
    onZoom?: (left: any, right: any) => void
    isZoomEnabled?: boolean
}) {
    const { chartData, selectedChart, setSelectedChart, options = ['Volume', 'TVL', 'Price'], onZoom, renderedChart = null, isZoomEnabled = false } = props
    const [leftZoom, setLeftZoom] = useState('dataMin')
    const [rightZoom, setRightZoom] = useState('dataMax')
    const [isZooming, setIsZooming] = useState(false)
    const [isZoomed, setIsZoomed] = useState(false)

    useEffect(() => {
        if (!isZooming && onZoom) {
            onZoom(Number(leftZoom), rightZoom !== 'dataMax' ? Number(rightZoom) : rightZoom)
            if (rightZoom !== 'dataMax') setIsZoomed(true)
        }
    }, [isZooming])

    useEffect(() => {
        setLeftZoom('dataMin')
        setRightZoom('dataMax')
    }, [chartData])

    function renderChartSelection(title: ChartTypes) {
        const isSelected = selectedChart === title
        return (
            <div
                key={title}
                onClick={() => setSelectedChart(title)}
                className={`text-[12px] cursor-pointer px-[3px] py-[2px] ${isSelected ? 'bg-[#0AE8F9] text-[#0D0C1E] rounded-[6px] ' : 'text-white'}`}>
                <span className=''>{title}</span>
            </div>
        )
    }

    function renderChartsSelector() {
        const renderedOptions = options.map(renderChartSelection)
        return <div className={`flex-1 flex justify-between bg-[#0C2960] max-w-[185px] items-center p-[6px] h-[33px] shadow-lg rounded-[12px]`}>{renderedOptions}</div>
    }

    const customizedGroupTick = (props: any) => {
        const { x, y, payload } = props
        return (
            <g>
                <g>
                    <text fill='#6D7391' dy={20} x={x} y={y}>
                        {formatTimestampDay(payload.value).toString()}
                    </text>
                </g>
            </g>
        )
    }

    function renderXAxis(dataKey = 'timestamp', left = 'dataMin', right = 'dataMax') {
        return (
            <XAxis
                allowDataOverflow={isZoomEnabled}
                // domain={[`auto`, 'auto']}
                type={isZoomEnabled ? 'number' : 'category'}
                domain={isZoomEnabled ? [left, right] : ['auto', 'auto']}
                dataKey={dataKey}
                axisLine={{ stroke: '#0AE8F9', fill: '#0AE8F9' }}
                tickFormatter={formatTimestamp}
                tickLine={{
                    stroke: '#0AE8F9',
                    strokeOpacity: 0.2,
                    strokeWidth: 0.2
                }}
                tick={{ fontSize: 12 }}
                dy={10}
                tickCount={6}
                // interval={'preserveStartEnd'}
                // minTickGap={1}
                // interval={'preserveStartEnd'}
            />
        )
    }

    function renderRefArea() {
        if (!isZooming || leftZoom === 'dataMin' || rightZoom === 'dataMax') return null
        return <ReferenceArea x1={Number(leftZoom)} x2={Number(rightZoom)} stroke='#549EFC' fill='#549EFC' />
    }

    function renderChart(dataKey = 'volume') {
        if (renderedChart) return renderedChart
        const ChrtType = selectedChart === 'Price' || selectedChart === 'TVL' ? LineChart : BarChart
        const ChartFill = selectedChart === 'Price' || selectedChart === 'TVL' ? Line : Bar

        const cursorObject =
            selectedChart === 'Volume'
                ? {
                      fill: '#549EFC',
                      strokeWidth: 0,
                      fillOpacity: 0.8
                  }
                : {
                      stroke: '#549EFC',
                      strokeWidth: 1,
                      strokeDasharray: '1 2'
                  }
        return (
            <ResponsiveContainer style={{ userSelect: 'none' }} className={`mt-[24px] ${''}`} width={'100%'} height={chartHeight}>
                {/* @ts-ignore */}
                <ChrtType
                    onMouseDown={(e) => {
                        if (!e || !e.activeLabel) return
                        isZoomEnabled ? setIsZooming(true) : null
                        isZoomEnabled ? setLeftZoom(e.activeLabel!.toString()) : null
                    }}
                    onMouseMove={(e) => {
                        if (!e || !e.activeLabel) return
                        isZooming && isZoomEnabled ? setRightZoom(e.activeLabel!.toString()) : null
                        if (chartData[chartData.length - 1] && Number(e.activeLabel) >= Number(chartData[chartData.length - 1].timestamp)) {
                            isZoomEnabled ? setIsZooming(false) : null
                        }
                    }}
                    onMouseUp={(e) => {
                        isZoomEnabled ? setIsZooming(false) : null
                    }}
                    barCategoryGap={2}
                    height={chartHeight}
                    margin={{ top: 0, left: 0, right: 0, bottom: 0 }}
                    barGap={0}
                    data={chartData}>
                    {renderXAxis('timestamp', leftZoom, rightZoom)}
                    {/* <YAxis  domain={['dataMin', 'dataMax']} height={chartHeight} hide={true} padding={{ bottom: 0 }} /> */}
                    {/* <Line dataKey={'measurement'} /> */}
                    <YAxis
                        allowDataOverflow={true}
                        tick={{ fontSize: 12 }}
                        dx={-5}
                        tickLine={{
                            fill: '#549EFC',
                            strokeWidth: 0,
                            fillOpacity: 0.8
                        }}
                        axisLine={{ stroke: 'transparent' }}
                        tickFormatter={(x) => {
                            return `$${formatDollarValue(x, 0)}`
                        }}
                        domain={['auto', 'auto']}
                        // domain={['auto', 'auto']}
                        height={chartHeight}
                        hide={!isZoomEnabled}
                        padding={{ bottom: 0 }}
                    />
                    {/* @ts-ignore */}
                    <CartesianGrid vertical={false} stroke='#549EFC' strokeWidth={1} strokeDasharray='5' opacity={0.2} />
                    {/* <CartesianAxis interval={'equidistantPreserveStart'} /> */}
                    {/* @ts-ignore */}
                    <ChartFill animationDuration={100} type='linear' dot={false} height={chartHeight} strokeWidth={2} dataKey={dataKey} opacity={1}>
                        {chartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} stroke={`#0AE8F9`} fill={`#0AE8F9`} />
                        ))}
                    </ChartFill>
                    {/* @ts-ignore */}
                    <Tooltip cursor={cursorObject} content={<TooltipVolume type={selectedChart} />} />
                    {/* <ReferenceArea x1={leftZoom} x2={rightZoom} stroke='#549EFC' fill='#549EFC' strokeOpacity={0.3} /> */}
                    {renderRefArea()}
                    {/* {isZooming && (leftZoom !== 'dataMin' || rightZoom !== 'dataMax') ? <ReferenceArea x1={leftZoom} x2={rightZoom} stroke='#549EFC' fill='#549EFC' /> : null} */}
                </ChrtType>
            </ResponsiveContainer>
        )
    }

    function renderZoomOutButton() {
        if (!isZoomEnabled) return null
        if (!isZoomed) return null

        return (
            <div
                className='text-[white] bg-[#0C2960] text-[12px] cursor-pointer p-2 rounded-md'
                onClick={() => {
                    setLeftZoom('dataMin')
                    setRightZoom('dataMax')
                    onZoom ? onZoom('dataMin', 'dataMax') : null
                    setIsZoomed(false)
                }}>
                Zoom out
            </div>
        )
    }
    function renderChartsContainer() {
        return (
            <div className='flex p-6 flex-col bg-[#0C1647] mt-[32px] rounded-[8px] w-full '>
                <div className='flex items-center w-full justify-between mb-[16px]'>
                    {renderChartsSelector()}
                    {renderZoomOutButton()}
                </div>
                {renderChart(selectedChart.toLowerCase())}
            </div>
        )
    }

    return renderChartsContainer()
}

export type TokenCharacteristics = {
    is_code_verified: 'verified' | 'unknown' | 'failed'
    is_none_upgradable: 'verified' | 'unknown' | 'failed'
    is_initial_liquidity_timelock: 'verified' | 'unknown' | 'failed'
    is_not_taxed: 'verified' | 'unknown' | 'failed'
}

export const TooltipVolume = ({ active, payload, label, type }: { active?: any; payload?: any; label?: any; type: ChartTypes }) => {
    const key = type.toLowerCase()
    const value = payload?.[0]?.payload?.[key]
    const timestamp = Number(label)
    if (!timestamp) return <></>
    if (!active || !value || !label || !timestamp) return <></>

    return (
        <div className='bg-[#242C58] text-[white] rounded-[8px] py-3 px-4 shadow-2xl opacity-[0.8]'>
            <div className='flex flex-col items-center'>
                <span>{`${[type]} = $${type === 'Price' ? numFormat(value) : formatDollarValue(value)}`}</span>
                <span>{`Date = ${formatTimestamp(timestamp)}`}</span>
            </div>
        </div>
    )
}
